/**
 * Hex encoding/decoding utilities.
 * Uses Uint8Array throughout — never Buffer.allocUnsafe.
 */

/**
 * Convert a hex string (with or without 0x prefix) to Uint8Array.
 * Throws on invalid input.
 */
export function hexToBytes(hex: string): Uint8Array {
  const cleanHex = hex.startsWith('0x') ? hex.slice(2) : hex;

  if (cleanHex.length % 2 !== 0) {
    throw new Error(`Invalid hex string: odd length (${cleanHex.length})`);
  }

  if (!/^[0-9a-fA-F]*$/.test(cleanHex)) {
    throw new Error('Invalid hex string: contains non-hex characters');
  }

  const bytes = new Uint8Array(cleanHex.length / 2);
  for (let i = 0; i < cleanHex.length; i += 2) {
    const byte = parseInt(cleanHex.substring(i, i + 2), 16);
    if (Number.isNaN(byte)) {
      throw new Error(`Invalid hex byte at position ${i}`);
    }
    bytes[i / 2] = byte;
  }

  return bytes;
}

/**
 * Convert Uint8Array to 0x-prefixed hex string.
 */
export function bytesToHex(bytes: Uint8Array): string {
  let hex = '0x';
  for (let i = 0; i < bytes.length; i++) {
    hex += (bytes[i] ?? 0).toString(16).padStart(2, '0');
  }
  return hex;
}

/**
 * Normalize a hex string to lowercase with 0x prefix.
 */
export function normalizeHex(hex: string): string {
  const clean = hex.startsWith('0x') ? hex : `0x${hex}`;
  return clean.toLowerCase();
}

/**
 * Check if two hex strings represent the same bytes (case-insensitive).
 * This is NOT constant-time — use constantTimeEqual for security-critical comparisons.
 */
export function hexEqual(a: string, b: string): boolean {
  return normalizeHex(a) === normalizeHex(b);
}
